import { useState } from "react";
import { api, CONTACT } from "@/lib/api";
import { Phone, MessageCircle, Mail } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);
    try {
      await api.post("/contact", form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus("error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-32 pb-24 px-6 md:px-12" data-testid="contact-page">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-20">
          <div className="label-luxury mb-4">{t("contact_eyebrow")}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl font-light tracking-tight">
            {t("contact_title_1")} <span className="italic gold-text">{t("contact_title_2")}</span>
          </h1>
          <p className="text-white/55 mt-6 max-w-xl mx-auto leading-relaxed">
            {t("contact_intro")}
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16">
          <div className="space-y-8">
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="block border border-white/10 p-8 hover:border-[var(--gold)]/60 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-luxury mb-1">{t("wa_preferred")}</div>
                  <div className="font-serif-display text-3xl mt-2">{CONTACT.display}</div>
                </div>
                <MessageCircle size={24} className="text-[var(--gold)]" />
              </div>
            </a>

            <a
              href={`viber://chat?number=%2B${CONTACT.viber}`}
              data-testid="contact-viber"
              className="block border border-white/10 p-8 hover:border-[var(--gold)]/60 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-luxury mb-1">{t("viber_label")}</div>
                  <div className="font-serif-display text-3xl mt-2">{CONTACT.display}</div>
                </div>
                <MessageCircle size={24} className="text-[var(--gold)]" />
              </div>
            </a>

            <a
              href={`tel:${CONTACT.phone}`}
              data-testid="contact-phone"
              className="block border border-white/10 p-8 hover:border-[var(--gold)]/60 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-luxury mb-1">{t("direct_line")}</div>
                  <div className="font-serif-display text-3xl mt-2">{CONTACT.display}</div>
                </div>
                <Phone size={24} className="text-[var(--gold)]" />
              </div>
            </a>

            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email"
              className="block border border-white/10 p-8 hover:border-[var(--gold)]/60 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-luxury mb-1">{t("email_label")}</div>
                  <div className="font-serif-display text-xl mt-2">{CONTACT.email}</div>
                </div>
                <Mail size={24} className="text-[var(--gold)]" />
              </div>
            </a>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-white/10 p-10 md:p-14 bg-[#0a0a0a]"
          >
            <div className="label-luxury mb-6">{t("send_message")}</div>

            <div className="space-y-8">
              <input
                data-testid="contact-name"
                required
                placeholder={t("your_name")}
                className="luxury-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                data-testid="contact-email-input"
                type="email"
                placeholder={t("email_optional")}
                className="luxury-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                data-testid="contact-phone-input"
                placeholder={t("phone_wa")}
                className="luxury-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <textarea
                data-testid="contact-message"
                required
                placeholder={t("tell_us")}
                rows={5}
                className="luxury-input resize-none"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                data-testid="contact-submit"
                disabled={sending}
                className="btn-gold btn-gold-solid disabled:opacity-50"
                type="submit"
              >
                {sending ? t("sending") : t("send_request")}
              </button>
              <p className="text-white/40 text-xs tracking-wider">
                {t("discretion")}
              </p>
            </div>

            {status === "success" && (
              <div
                data-testid="contact-success"
                className="mt-8 border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-5 text-[var(--gold)] text-sm tracking-wider"
              >
                {t("success_msg")}
              </div>
            )}
            {status === "error" && (
              <div className="mt-8 border border-red-500/40 bg-red-500/5 p-5 text-red-300 text-sm tracking-wider">
                {t("error_msg")}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
